import React from 'react';
import { Highlight } from '../types';

interface TextHighlighterProps {
  text: string;
  highlights: Highlight[];
}

const TextHighlighter: React.FC<TextHighlighterProps> = ({ text, highlights }) => {
  // Sort highlights by occurrence to prevent issues, though simple string replacement is safer for display
  // We will build an array of React Nodes.
  
  if (!highlights || highlights.length === 0) {
    return <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{text}</p>;
  }

  // Simple strategy: Split text by the substrings found. 
  // Note: This simple version assumes unique substrings or highlights all occurrences. 
  // A more robust version would use indices, but this is effective for a demo.
  
  let parts: React.ReactNode[] = [text];

  highlights.forEach((highlight, hIndex) => {
    const nextParts: React.ReactNode[] = [];
    parts.forEach((part) => {
      if (typeof part === 'string') {
        const splitPart = part.split(highlight.substring);
        splitPart.forEach((fragment, i) => {
          nextParts.push(fragment);
          if (i < splitPart.length - 1) {
            // Insert highlight
            const isPositive = highlight.type === 'positive';
            nextParts.push(
              <span key={`${hIndex}-${i}`} className="group relative inline-block cursor-help">
                <span className={`
                  px-1 rounded 
                  ${isPositive 
                    ? 'bg-emerald-100 text-emerald-800 border-b-2 border-emerald-300' 
                    : 'bg-red-100 text-red-800 border-b-2 border-red-300'}
                `}>
                  {highlight.substring}
                </span>
                
                {/* Tooltip */}
                <span className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-10 text-center">
                  {highlight.explanation}
                  <svg className="absolute text-slate-800 h-2 w-full left-0 top-full" x="0px" y="0px" viewBox="0 0 255 255">
                    <polygon className="fill-current" points="0,0 127.5,127.5 255,0"/>
                  </svg>
                </span>
              </span>
            );
          }
        });
      } else {
        nextParts.push(part);
      }
    });
    parts = nextParts;
  });

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-4">Diagnóstico Visual</h3>
      <div className="text-slate-700 leading-relaxed whitespace-pre-wrap font-medium">
        {parts}
      </div>
      <div className="mt-4 flex gap-4 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-red-400"></span>
          <span className="text-slate-500">Complejo / Frío</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
          <span className="text-slate-500">Claro / Empático</span>
        </div>
      </div>
    </div>
  );
};

export default TextHighlighter;